import { useEffect, useRef } from 'react';

import { ProcessStatuses, useGetProcessStatusesQuery, useLazyGetProcessStatusesQuery } from 'store/api/processes';

const POLLING_INTERVAL_MS = 5000;

interface UseProcessStatusPollingResult {
    statuses: ProcessStatuses | undefined;
    isLoading: boolean;
    anyRunning: boolean;
}

/**
 * Subscribes to process statuses and polls every 5s while any process is running.
 * Each call site owns its own polling lifecycle — callers do not need to share a parent.
 */
export const useProcessStatusPolling = (): UseProcessStatusPollingResult => {
    const { data: statuses, isLoading } = useGetProcessStatusesQuery();
    const [fetchStatuses] = useLazyGetProcessStatusesQuery();
    const pollingIntervalRef = useRef<NodeJS.Timeout | undefined>(undefined);

    const anyRunning = statuses ? Object.values(statuses).some((running) => running) : false;

    useEffect(() => {
        if (anyRunning) {
            if (pollingIntervalRef.current) {
                clearInterval(pollingIntervalRef.current);
            }
            pollingIntervalRef.current = setInterval(() => {
                fetchStatuses();
            }, POLLING_INTERVAL_MS);
        } else if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = undefined;
        }

        return () => {
            if (pollingIntervalRef.current) {
                clearInterval(pollingIntervalRef.current);
            }
        };
    }, [anyRunning, fetchStatuses]);

    return { statuses, isLoading, anyRunning };
};
